import React, { useMemo } from 'react';
import { Box } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { RewardHistoryNode } from './RewardHistoryNode';
import { useGetRewardsHistory } from '../hooks/useGetRewardsHistory';

interface RewardHistoryProps {
  accountId: string;
}

export const RewardHistory = ({ accountId }: RewardHistoryProps): JSX.Element => {
  const { rewardsHistory, rewardsHistoryCount, loading, fetchMore } = useGetRewardsHistory(accountId);

  const rewardsHistoryCopy = useMemo(() => [...rewardsHistory], [rewardsHistory]);
  const sortedRewardsHistory = useMemo(
    () =>
      rewardsHistoryCopy?.sort((a, b) => {
        const dateA = a.relatedRedemptionHistoryEntry?.redemption.updatedAt || a.createdAt;
        const dateB = b.relatedRedemptionHistoryEntry?.redemption.updatedAt || b.createdAt;
        return dateB - dateA;
      }),
    [rewardsHistoryCopy],
  );

  // Filters out duplicate cancelled history entries
  const filteredRewardsHistory = useMemo(() => {
    const redemptionIds: string[] = [];

    return sortedRewardsHistory.filter((rewardHistory) => {
      const currentRedemptionId = rewardHistory.relatedRedemptionHistoryEntry?.redemption.id;
      if (currentRedemptionId) {
        const shouldBeFiltered = redemptionIds.includes(currentRedemptionId);
        redemptionIds.push(currentRedemptionId);
        return !shouldBeFiltered;
      }

      return true;
    });
  }, [sortedRewardsHistory]);

  return (
    <Box py={3} px={4}>
      <Text as="p" fontWeight="bold" size="lg" mb={3}>
        Rewards History
      </Text>
      <Box>
        {filteredRewardsHistory?.map((node) => {
          return <RewardHistoryNode key={node.id} node={node} />;
        })}
        {rewardsHistory?.length !== rewardsHistoryCount && (
          <Button
            variant="ghost"
            my={2}
            isFullWidth
            size="sm"
            isLoading={loading}
            isDisabled={loading}
            onClick={(): Promise<void> => fetchMore()}
          >
            Show More
          </Button>
        )}
        {!rewardsHistory?.length && !loading && (
          <Text as="p" size="sm" variant="subtle" textAlign="center">
            There&apos;s no history to show
          </Text>
        )}
      </Box>
    </Box>
  );
};
